import {
  Button,
  Checkbox,
  Field,
  Input,
  Select,
  Spinner,
  Textarea,
  makeStyles,
  tokens,
  Text,
} from "@fluentui/react-components";
import { CheckmarkCircleRegular } from "@fluentui/react-icons";
import axios from "axios";
import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BrandLogo } from "../logo/BrandLogo";

const useStyles = makeStyles({
  page: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    backgroundColor: tokens.colorNeutralBackground2,
  },
  logoBar: {
    width: "100%",
    padding: "20px 24px",
    boxSizing: "border-box",
  },
  card: {
    width: "100%",
    maxWidth: "520px",
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    padding: "32px 28px",
    boxSizing: "border-box",
    marginBottom: "40px",
  },
  heading: {
    marginBottom: tokens.spacingVerticalXL,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
  },
  actions: {
    marginTop: tokens.spacingVerticalS,
  },
  success: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: tokens.spacingVerticalL,
    paddingTop: tokens.spacingVerticalXXL,
    paddingBottom: tokens.spacingVerticalXXL,
    textAlign: "center",
  },
  successIcon: {
    fontSize: "56px",
    color: tokens.colorPaletteGreenForeground1,
  },
});

type SupportForm = {
  reportedByName: string;
  title: string;
  customerName: string;
  orderNumber: string;
  phoneNumber: string;
  email: string;
  category: string;
  componentName: string;
  faultSymptom: string;
  replacementMaterial: boolean;
};

export const SupportPage = (): JSX.Element => {
  const styles = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlOrder = searchParams.get("order") ?? "";
  const urlCustomer = searchParams.get("customer") ?? "";
  const [form, setForm] = useState<SupportForm>({
    reportedByName: "",
    title: "",
    customerName: urlCustomer,
    orderNumber: urlOrder,
    phoneNumber: "",
    email: "",
    category: "",
    componentName: "",
    faultSymptom: "",
    replacementMaterial: false,
  });
  const [image, setImage] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validatingCustomer, setValidatingCustomer] = useState(!!urlCustomer);
  const [submitted, setSubmitted] = useState(false);
  const [ticketNumber, setTicketNumber] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isValid =
    form.reportedByName.trim() !== "" &&
    form.title.trim() !== "" &&
    form.orderNumber.trim() !== "" &&
    form.phoneNumber.trim() !== "" &&
    form.email.trim() !== "" &&
    form.category !== "" &&
    form.componentName.trim() !== "" &&
    form.faultSymptom.trim() !== "";

  const lockedOrder = urlOrder !== "";
  const lockedCustomer = urlCustomer !== "";

  useEffect(() => {
    setForm((prev) => ({ ...prev, customerName: urlCustomer }));
  }, [urlCustomer]);

  useEffect(() => {
    if (!urlCustomer) return;
    axios
      .get(`${process.env.REACT_APP_API_URL}/support/validate-customer`, { params: { customerName: urlCustomer } })
      .then((res) => {
        if (!res.data) {
          navigate(`/support${urlOrder ? `?order=${urlOrder}` : ""}`, { replace: true });
        } else {
          setValidatingCustomer(false);
        }
      })
      .catch(() => navigate(`/support${urlOrder ? `?order=${urlOrder}` : ""}`, { replace: true }));
  }, [urlCustomer]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append("reportedByName", form.reportedByName);
      formData.append("title", form.title);
      formData.append("customerName", form.customerName);
      formData.append("orderNumber", form.orderNumber);
      formData.append("phoneNumber", form.phoneNumber);
      formData.append("email", form.email);
      formData.append("category", form.category);
      formData.append("componentName", form.componentName);
      formData.append("faultSymptom", form.faultSymptom);
      formData.append("replacementMaterial", String(form.replacementMaterial));
      if (image) formData.append("image", image, image.name);

      const response = await axios.post(`${process.env.REACT_APP_API_URL}/support/create-ticket`, formData);
      setTicketNumber(response.data.ticketNumber);
      setSubmitted(true);
    } catch {
      setError(t("error.ticketCreate"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setForm({
      reportedByName: "",
      title: "",
      customerName: urlCustomer,
      orderNumber: urlOrder,
      phoneNumber: "",
      email: "",
      category: "",
      componentName: "",
      faultSymptom: "",
      replacementMaterial: false,
    });
    setImage(null);
    setSubmitted(false);
    setTicketNumber(null);
    setError(null);
  };

  return (
    <div className={styles.page}>
      <div className={styles.logoBar}>
        <BrandLogo />
      </div>
      <div className={styles.card}>
        {submitted ? (
          <div className={styles.success}>
            <CheckmarkCircleRegular className={styles.successIcon} />
            <Text size={600} weight="semibold">
              {t("success.ticketCreated", { number: ticketNumber })}
            </Text>
            <Button appearance="secondary" onClick={handleReset}>
              {t("buttons.createTicket")}
            </Button>
          </div>
        ) : (
          <>
            <div className={styles.heading}>
              <Text size={700} weight="semibold" block>
                {t("buttons.createTicket")}
              </Text>
            </div>
            <div className={styles.form}>
              <Field label={t("labels.yourName")} required>
                <Input
                  value={form.reportedByName}
                  onChange={(e) => setForm((p) => ({ ...p, reportedByName: e.target.value }))}
                />
              </Field>
              <Field label={t("labels.title")} required>
                <Input
                  value={form.title}
                  onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                />
              </Field>
              {!(lockedCustomer && form.customerName && !validatingCustomer) ? null : (
                <Field label={t("labels.customer")}>
                  <Input
                    value={form.customerName}
                    disabled={lockedCustomer}
                    onChange={(e) => setForm((p) => ({ ...p, customerName: e.target.value }))}
                  />
                </Field>
              )}
              <Field label={t("labels.orderNumber")} required>
                <Input
                  value={form.orderNumber}
                  disabled={lockedOrder}
                  onChange={(e) => setForm((p) => ({ ...p, orderNumber: e.target.value }))}
                />
              </Field>
              <Field label={t("labels.phoneNumber")} required>
                <Input
                  type="tel"
                  value={form.phoneNumber}
                  onChange={(e) => setForm((p) => ({ ...p, phoneNumber: e.target.value }))}
                />
              </Field>
              <Field label={t("labels.email")} required>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                />
              </Field>
              <Field label={t("labels.category")} required>
                <Select
                  value={form.category}
                  onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}
                  select={{
                    style: { color: form.category === "" ? tokens.colorNeutralForeground3 : "inherit" },
                  }}
                >
                  <option value="" disabled>{t("labels.categoryPlaceholder")}</option>
                  <option value="Elektronik">{t("labels.electronics")}</option>
                  <option value="Mekanik">{t("labels.mechanics")}</option>
                  <option value="Material i leverans">{t("labels.materialInDelivery")}</option>
                </Select>
              </Field>
              <Field label={t("labels.componentName")} required>
                <Input
                  value={form.componentName}
                  onChange={(e) => setForm((p) => ({ ...p, componentName: e.target.value }))}
                />
              </Field>
              <Field label={t("labels.faultSymptom")} required>
                <Textarea
                  value={form.faultSymptom}
                  onChange={(e) => setForm((p) => ({ ...p, faultSymptom: e.target.value }))}
                  resize="vertical"
                />
              </Field>
              <Field hint={t("labels.replacementMaterialHint")}>
                <Checkbox
                  checked={form.replacementMaterial}
                  onChange={(_, data) => setForm((p) => ({ ...p, replacementMaterial: !!data.checked }))}
                  label={t("labels.replacementMaterialWanted")}
                />
              </Field>
              <Field label={t("labels.image")}>
                <input type="file" accept="image/*" onChange={(e) => setImage(e.target.files?.[0] ?? null)} />
              </Field>
              {error && (
                <Text style={{ color: tokens.colorPaletteRedForeground1 }}>{error}</Text>
              )}
              <div className={styles.actions}>
                <Button
                  appearance="primary"
                  disabled={!isValid || isSubmitting}
                  onClick={handleSubmit}
                  icon={isSubmitting ? <Spinner size="tiny" /> : undefined}
                >
                  {t("buttons.create")}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
